import React, { useContext } from 'react'
import { ModalDialogContext } from '@/app/common/context/modalDialogContext'
import { getFeesScheduleLink } from '@/config'
import { FeeInfoModal } from '@/domains/checkout/components/FeeInfoModal/FeeInfoModal'
import { QuestionMark } from '@/domains/checkout/components/QuestionMark'
import useRegionCode from '@/helpers/useRegionCode'
import { EVehicleCategory, SelectedItemsType } from '@/types'
import {
  DealValuesEntry,
  EBusinessUnit,
  EDealType,
  TransportData,
  UsedFeeDefinition,
} from '@/types/gql/graphql'
import { cn } from '@/utils/cn'
import { printLocalAmount } from '@/utils/misc'

export interface DealValues {
  value: number
  label: string
  last: boolean
  hideDecimalPlaces: boolean
  text?: string
  hasQuestionMark: boolean
  originalFees?: boolean
}

interface DealValuesTableProps {
  className?: string
  dealValues: Array<DealValues | undefined>
  appliedUsedFeeDefinitions?: UsedFeeDefinition[] | undefined
  manipulatedAppliedUsedFeeDefinitions?: UsedFeeDefinition[] | undefined
  transportData?: TransportData
  title?: string
  customItemTitles?: string[]
  embedded?: boolean
  dealItems?: Omit<SelectedItemsType, 'answers' | 'itemCategory'>[]
  dealValuesEntry?: DealValuesEntry | undefined
  showKnp?: boolean | undefined
  isDropOff?: boolean
  totalFees?: number
  dealType?: EDealType
}

export function DealValuesTable({
  className,
  dealValues,
  appliedUsedFeeDefinitions,
  manipulatedAppliedUsedFeeDefinitions,
  title,
  customItemTitles,
  dealItems,
  totalFees,
  dealType,
}: DealValuesTableProps) {
  return (
    <>
      {title && (
        <h2 className="text-heading-2xs text-tertiary uppercase mb-1">
          {title}
        </h2>
      )}
      <div
        className={cn(
          'bg-white px-4 pt-1.5 rounded-xl border border-secondary',
          className,
        )}
      >
        {dealValues
          .filter((c) => Boolean(c))
          .map((dealValue, index) => {
            return (
              <DealValueRow
                id={`DEAL_VALUE_ROW_BUTTON_${index}`}
                key={index}
                value={dealValue?.value ?? 0}
                label={dealValue?.label ?? ''}
                last={dealValue?.last}
                appliedUsedFeeDefinitions={appliedUsedFeeDefinitions}
                manipulatedAppliedUsedFeeDefinitions={
                  manipulatedAppliedUsedFeeDefinitions
                }
                customItemTitles={customItemTitles}
                hideDecimalPlaces={dealValue?.hideDecimalPlaces}
                text={dealValue?.text}
                hasQuestionMark={dealValue?.hasQuestionMark}
                dealItems={dealItems}
                totalFees={totalFees}
                originalFees={dealValue?.originalFees}
                dealType={dealType}
              />
            )
          })}
      </div>
    </>
  )
}

interface DealValueRowProps {
  id?: string
  label: string
  value: number
  text?: string
  last?: boolean
  appliedUsedFeeDefinitions?: UsedFeeDefinition[] | undefined
  manipulatedAppliedUsedFeeDefinitions?: UsedFeeDefinition[] | undefined
  hideDecimalPlaces?: boolean
  customItemTitles?: string[]
  hasQuestionMark?: boolean
  dealItems?: Omit<SelectedItemsType, 'answers' | 'itemCategory'>[]
  totalFees?: number
  originalFees?: boolean
  dealType?: EDealType
}

function DealValueRow(props: DealValueRowProps) {
  const modal = useContext(ModalDialogContext)
  const {
    id,
    label,
    value,
    text,
    last,
    hideDecimalPlaces,
    customItemTitles,
    dealItems,
    hasQuestionMark,
    appliedUsedFeeDefinitions,
    manipulatedAppliedUsedFeeDefinitions,
    totalFees,
    originalFees,
    dealType,
  } = props
  const regionCode = useRegionCode()

  const businessUnit =
    dealItems &&
    dealItems.length > 0 &&
    Object.values(EVehicleCategory).includes(dealItems[0].itemCategoryId)
      ? EBusinessUnit.Car
      : EBusinessUnit.General

  return (
    <div
      className={cn(
        'flex flex-1 flex-row justify-between py-2.5',
        last
          ? 'text-l font-semibold text-brand-secondary'
          : 'text-m text-secondary border-b border-secondary',
      )}
    >
      <div>
        {label}
        {hasQuestionMark &&
          manipulatedAppliedUsedFeeDefinitions?.length &&
          !text && (
            <QuestionMark
              id={id}
              onClick={() =>
                modal.open(
                  <FeeInfoModal
                    businessUnit={businessUnit}
                    appliedUsedFeeDefinitions={
                      originalFees
                        ? appliedUsedFeeDefinitions
                        : manipulatedAppliedUsedFeeDefinitions
                    }
                    dealItems={dealItems}
                    totalFees={totalFees}
                    customItemTitles={customItemTitles}
                    originalFees={originalFees}
                    dealType={dealType}
                    feeDocumentLink={getFeesScheduleLink(
                      regionCode,
                      businessUnit,
                    )}
                  />,
                  {
                    customStyles: {
                      content: {
                        display: 'inline-block',
                        maxWidth: '80%',
                      },
                    },
                  },
                )
              }
            />
          )}
      </div>
      {hasQuestionMark && text && (
        <QuestionMark
          onClick={() =>
            modal.open(<FeeInfoModal text={text} />, {
              customStyles: {
                content: {
                  display: 'inline-block',
                  maxWidth: '80%',
                },
              },
            })
          }
        />
      )}
      {!originalFees && (
        <div className="font-semibold">
          {printLocalAmount({
            number: value,
            fractionDigits: hideDecimalPlaces ? 0 : 2,
            removeSpaces: true,
          })}
        </div>
      )}
    </div>
  )
}
